/**
 * MapCanvas — the single long-lived Mapbox GL map instance.
 *
 * Owns exactly one map. Initializes once, disposes once, and updates
 * sources/layers imperatively. Never recreates the map on prop changes.
 *
 * Lifecycle-safe: all data, visibility, selection, and admin boundaries
 * are restored after basemap style changes.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp';
import 'mapbox-gl/dist/mapbox-gl.css';

import {
  getMapboxToken,
  validateMapboxConfig,
  BASEMAP_STYLES,
  RWANDA_CENTER,
  RWANDA_ZOOM,
  MIN_ZOOM,
  MAX_ZOOM,
  RWANDA_BOUNDS,
} from '../config/mapboxConfig';
import type { BasemapStyle, GeoCoord, MapViewport, LayerVisibility } from '../types/mapTypes';
import type { MapField, MapTree, MapFarmer, MapCohort, MapWarehouse } from '../types/mapTypes';
import { safeMapOperation } from '../utils/mapErrors';

import { addFieldLayers, updateFieldData, removeFieldLayers, setFieldSelected } from '../layers/fieldLayer';
import { addTreeLayers, updateTreeData, removeTreeLayers, setTreeSelected, zoomIntoCluster } from '../layers/treeLayer';
import { addFarmerLayers, updateFarmerData, removeFarmerLayers } from '../layers/farmerLayer';
import { addCohortLayers, updateCohortData, removeCohortLayers } from '../layers/cohortLayer';
import { addWarehouseLayers, updateWarehouseData, removeWarehouseLayers } from '../layers/warehouseLayer';
import { setAdminBoundaryLevel, removeAdminBoundaries } from '../layers/adminBoundaryLayer';
import type { BoundaryLevel } from '../layers/adminBoundaryLayer';

// ─── Drawing mode constants ──────────────────────────────────────────

const DRAW_SOURCE = 'draw-boundary';
const DRAW_FILL = 'draw-fill';
const DRAW_LINE = 'draw-line';
const DRAW_POINTS = 'draw-points';

function ensureDrawLayers(map: mapboxgl.Map) {
  if (map.getSource(DRAW_SOURCE)) return;
  map.addSource(DRAW_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({ id: DRAW_FILL, type: 'fill', source: DRAW_SOURCE, paint: { 'fill-color': '#3B82F6', 'fill-opacity': 0.15 } });
  map.addLayer({ id: DRAW_LINE, type: 'line', source: DRAW_SOURCE, paint: { 'line-color': '#3B82F6', 'line-width': 2.5, 'line-dasharray': [3, 2] } });
  map.addLayer({
    id: DRAW_POINTS, type: 'circle', source: DRAW_SOURCE,
    paint: { 'circle-radius': 6, 'circle-color': '#3B82F6', 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' },
  });
}

/**
 * Update draw preview on the map.
 * boundary is GeoCoord[] = [lng, lat][].
 */
function updateDrawData(map: mapboxgl.Map, boundary: GeoCoord[]) {
  if (!map.getSource(DRAW_SOURCE)) return;

  const features: any[] = [];

  // Draw points for each vertex
  if (boundary.length > 0) {
    features.push({
      type: 'Feature',
      id: 'draw-vertices',
      geometry: { type: 'MultiPoint', coordinates: boundary }, // GeoCoord is already [lng, lat]
      properties: {},
    });
  }

  // Draw polygon if we have 3+ points
  if (boundary.length >= 3) {
    const coords = [...boundary, boundary[0]]; // close ring
    features.push({
      type: 'Feature',
      id: 'draw-polygon',
      geometry: { type: 'Polygon', coordinates: [coords] },
      properties: {},
    });
  }

  (map.getSource(DRAW_SOURCE) as any).setData({ type: 'FeatureCollection', features });
}

function removeDrawLayers(map: mapboxgl.Map) {
  try {
    if (map.getLayer(DRAW_POINTS)) map.removeLayer(DRAW_POINTS);
    if (map.getLayer(DRAW_LINE)) map.removeLayer(DRAW_LINE);
    if (map.getLayer(DRAW_FILL)) map.removeLayer(DRAW_FILL);
    if (map.getSource(DRAW_SOURCE)) map.removeSource(DRAW_SOURCE);
  } catch { /* ignore */ }
}

// ─── Props ──────────────────────────────────────────────────────────

interface MapCanvasProps {
  // Viewport
  viewport: MapViewport;
  navigateRequested: boolean;
  basemap?: BasemapStyle;
  mode?: 'plan' | 'operate' | 'terrain3d';
  adminBoundaryLevel?: BoundaryLevel;

  // Data
  fields: MapField[];
  trees: MapTree[];
  farmers: MapFarmer[];
  cohorts: MapCohort[];
  warehouses: MapWarehouse[];

  // Visibility
  visibility: LayerVisibility;

  // Selection
  selectedFieldId?: number | null;
  selectedTreeId?: number | null;

  // Events
  onViewportChange?: (viewport: MapViewport) => void;
  onFieldClick?: (field: MapField) => void;
  onTreeClick?: (tree: MapTree) => void;
  onClusterClick?: (clusterId: number) => void;
  onFarmerClick?: (farmer: MapFarmer) => void;
  onCohortClick?: (cohort: MapCohort) => void;
  onWarehouseClick?: (warehouse: MapWarehouse) => void;
  onMapReady?: (map: mapboxgl.Map) => void;

  // Drawing mode
  drawingMode?: boolean;
  draftBoundary?: GeoCoord[];
  onDraftBoundaryChange?: (boundary: GeoCoord[]) => void;

  className?: string;
}

// ─── Component ──────────────────────────────────────────────────────

const MapCanvas: React.FC<MapCanvasProps> = ({
  viewport,
  navigateRequested,
  basemap = 'satellite',
  mode = 'plan',
  adminBoundaryLevel = 'none',
  fields,
  trees,
  farmers,
  cohorts,
  warehouses,
  visibility,
  selectedFieldId,
  selectedTreeId,
  onViewportChange,
  onFieldClick,
  onTreeClick,
  onClusterClick,
  onFarmerClick,
  onCohortClick,
  onWarehouseClick,
  onMapReady,
  drawingMode = false,
  draftBoundary = [],
  onDraftBoundaryChange,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const navigatingRef = useRef(false);
  const restoredRef = useRef(false); // guard against double restoration
  const readyRef = useRef(false);
  const appliedStyleRef = useRef(BASEMAP_STYLES[basemap]);
  // Bumped on every style.load so data effects re-run after basemap switches.
  // Without this, switching styles removes all sources/layers but the data
  // effects don't re-fire (their deps haven't changed), so data vanishes.
  const [styleVersion, setStyleVersion] = useState(0);

  // Store latest data in refs so style-load handler can restore
  const dataRef = useRef({ fields, trees, farmers, cohorts, warehouses, visibility, adminBoundaryLevel, selectedFieldId, selectedTreeId });
  dataRef.current = { fields, trees, farmers, cohorts, warehouses, visibility, adminBoundaryLevel, selectedFieldId, selectedTreeId };

  // Store drawing callbacks
  const drawRef = useRef({ drawingMode, draftBoundary, onDraftBoundaryChange });
  drawRef.current = { drawingMode, draftBoundary, onDraftBoundaryChange };

  // Store interaction callbacks
  const callbacksRef = useRef({ onFieldClick, onTreeClick, onClusterClick, onFarmerClick, onCohortClick, onWarehouseClick });
  callbacksRef.current = { onFieldClick, onTreeClick, onClusterClick, onFarmerClick, onCohortClick, onWarehouseClick };

  // Track polling intervals for cleanup
  const restorePollRef = useRef<ReturnType<typeof setInterval> | null>(null);



  // ── Helper: restore all data after style change ──────────────
  const restoreAllData = useCallback((map: mapboxgl.Map) => {
    const d = dataRef.current;

    // Restore each operational layer independently. A malformed optional layer
    // must never prevent valid fields, trees, or facilities from rendering.
    const restoreLayer = (name: string, add: () => void, update: () => void) => {
      safeMapOperation(add, `restore:${name}:layers`);
      safeMapOperation(update, `restore:${name}:data`);
    };
    restoreLayer('fields', () => addFieldLayers(map), () => updateFieldData(map, d.fields));
    restoreLayer('trees', () => addTreeLayers(map), () => updateTreeData(map, d.trees));
    restoreLayer('farmers', () => addFarmerLayers(map), () => updateFarmerData(map, d.farmers));
    restoreLayer('cohorts', () => addCohortLayers(map), () => updateCohortData(map, d.cohorts));
    restoreLayer('warehouses', () => addWarehouseLayers(map), () => updateWarehouseData(map, d.warehouses));

    // Restore visibility
    const setLayerVisibility = (layerId: string, visible: boolean) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
      }
    };
    setLayerVisibility('fields-fill', d.visibility.fields);
    setLayerVisibility('fields-line', d.visibility.fields);
    setLayerVisibility('fields-label', d.visibility.fields);
    setLayerVisibility('fields-center', d.visibility.fields);
    setLayerVisibility('tree-clusters', d.visibility.trees);
    setLayerVisibility('tree-cluster-count', d.visibility.trees);
    setLayerVisibility('tree-points', d.visibility.trees);
    setLayerVisibility('farmers-fill', d.visibility.farmers);
    setLayerVisibility('farmers-line', d.visibility.farmers);
    setLayerVisibility('cohorts-fill', d.visibility.cohorts);
    setLayerVisibility('cohorts-line', d.visibility.cohorts);
    setLayerVisibility('cohorts-label', d.visibility.cohorts);
    setLayerVisibility('warehouse-circles', d.visibility.warehouses);
    setLayerVisibility('warehouse-labels', d.visibility.warehouses);

    // Restore admin boundaries
    safeMapOperation(
      () => setAdminBoundaryLevel(map, d.adminBoundaryLevel),
      'restore:adminBoundaries',
    );

    // Restore selection
    safeMapOperation(() => setFieldSelected(map, d.selectedFieldId ?? null), 'restore:fieldSelection');
    safeMapOperation(() => setTreeSelected(map, d.selectedTreeId ?? null), 'restore:treeSelection');

    // Re-setup interactions (guarded by dedup flag)
    safeMapOperation(() => setupInteractions(map), 'restore:interactions');

    // Restore draw layers if in drawing mode
    if (drawRef.current.drawingMode) {
      safeMapOperation(() => {
        ensureDrawLayers(map);
        updateDrawData(map, drawRef.current.draftBoundary);
      }, 'restore:drawing');
    }
    // All mutable data and callbacks are intentionally read from refs so the
    // style listener remains stable for the lifetime of this map instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Initialize map ──────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const config = validateMapboxConfig();
    if (!config.valid) {
      console.error('[MapCanvas]', config.error);
      return;
    }

    mapboxgl.accessToken = getMapboxToken();
    mapboxgl.workerUrl = `${process.env.PUBLIC_URL || ''}/mapbox-gl-csp-worker.js`;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: BASEMAP_STYLES[basemap],
      center: RWANDA_CENTER,
      zoom: RWANDA_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      maxBounds: [
        [RWANDA_BOUNDS[0] - 5, RWANDA_BOUNDS[1] - 5],
        [RWANDA_BOUNDS[2] + 5, RWANDA_BOUNDS[3] + 5],
      ],
      attributionControl: true,
      pitchWithRotate: true,
      antialias: true,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'bottom-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left');

    // Restore all data whenever the style (re)loads — this handles both
    // initial load and basemap switches (which strip custom sources/layers).
    const doRestore = () => {
      map.resize();
      restoredRef.current = true;
      restoreAllData(map);
      // Bump the version so data effects re-run and push data to the
      // freshly-created sources.
      setStyleVersion((v) => v + 1);
      if (!readyRef.current) {
        readyRef.current = true;
        onMapReady?.(map);
      }
    };

    const onStyleLoad = () => {
      // A style event can precede the final loaded state during a basemap
      // switch. Wait for a genuinely loaded style; never force layer creation
      // against an absent or failed style.
      if (restorePollRef.current) clearInterval(restorePollRef.current);
      if (map.isStyleLoaded()) {
        doRestore();
      } else {
        const start = Date.now();
        restorePollRef.current = setInterval(() => {
          if (map.isStyleLoaded()) {
            if (restorePollRef.current) clearInterval(restorePollRef.current);
            restorePollRef.current = null;
            doRestore();
          } else if (Date.now() - start > 30000) {
            if (restorePollRef.current) clearInterval(restorePollRef.current);
            restorePollRef.current = null;
            console.warn('[MapCanvas] Map style did not finish loading within 30 seconds, forcing restore');
            doRestore();
          }
        }, 200);
      }
    };

    map.on('style.load', onStyleLoad);

    // The map lives inside a flex workspace whose dimensions change when
    // drawers and responsive controls open. Mapbox does not observe those
    // layout changes itself, so keep its canvas matched to the container.
    const resizeObserver = new ResizeObserver(() => {
      if (!map.getContainer().isConnected) return;
      map.resize();
    });
    resizeObserver.observe(containerRef.current);
    const initialResizeFrame = requestAnimationFrame(() => map.resize());

    // Viewport change handler
    map.on('moveend', () => {
      if (navigatingRef.current) {
        navigatingRef.current = false;
        return;
      }
      const center = map.getCenter();
      onViewportChange?.({
        center: [center.lng, center.lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      });
    });

    // WebGL context loss recovery
    map.on('error', (e) => {
      if (e.error?.message?.includes('context lost')) {
        console.warn('[MapCanvas] WebGL context lost, attempting recovery');
        setTimeout(() => {
          if (map.getContainer()) map.resize();
        }, 1000);
        return;
      }
      console.error('[MapCanvas] Mapbox runtime error:', e.error ?? e);
    });

    mapRef.current = map;

    return () => {
      cancelAnimationFrame(initialResizeFrame);
      resizeObserver.disconnect();
      map.off('style.load', onStyleLoad);
      removeFieldLayers(map);
      removeTreeLayers(map);
      removeFarmerLayers(map);
      removeCohortLayers(map);
      removeWarehouseLayers(map);
      removeAdminBoundaries(map);
      removeDrawLayers(map);
      if (restorePollRef.current) clearInterval(restorePollRef.current);
      map.remove();
      mapRef.current = null;
      interactionGuardRef.current = false;
      restoredRef.current = false;
      readyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Setup interaction handlers ──────────────────────────────
  // Uses a guard flag so repeated calls from restoreAllData don't
  // stack up duplicate handlers.
  const interactionGuardRef = useRef(false);

  const setupInteractions = useCallback((map: mapboxgl.Map) => {
    // Prevent duplicate handlers
    if (interactionGuardRef.current) return;
    interactionGuardRef.current = true;

    // Field click — look up full entity from dataRef
    map.on('click', 'fields-fill', (e) => {
      if (!e.features?.length) return;
      const id = e.features[0].properties?.id;
      const field = dataRef.current.fields.find((f) => f.id === id);
      if (field) callbacksRef.current.onFieldClick?.(field);
    });

    map.on('click', 'fields-center', (e) => {
      if (!e.features?.length) return;
      const id = Number(e.features[0].properties?.id);
      const field = dataRef.current.fields.find((f) => f.id === id);
      if (field) callbacksRef.current.onFieldClick?.(field);
    });

    // Tree point click — look up full entity from dataRef
    map.on('click', 'tree-points', (e) => {
      if (!e.features?.length) return;
      const id = e.features[0].properties?.id;
      const tree = dataRef.current.trees.find((t) => t.id === id);
      if (tree) callbacksRef.current.onTreeClick?.(tree);
    });

    // Cluster click — zoom into it
    map.on('click', 'tree-clusters', (e) => {
      if (!e.features?.length) return;
      const clusterId = e.features[0].properties?.cluster_id;
      if (clusterId != null) {
        zoomIntoCluster(map, clusterId);
        callbacksRef.current.onClusterClick?.(clusterId);
      }
    });

    // Farmer click — look up full entity from dataRef
    map.on('click', 'farmers-fill', (e) => {
      if (!e.features?.length) return;
      const id = e.features[0].properties?.id;
      const farmer = dataRef.current.farmers.find((f) => f.id === id);
      if (farmer) callbacksRef.current.onFarmerClick?.(farmer);
    });

    // Cohort click — look up full entity from dataRef
    map.on('click', 'cohorts-fill', (e) => {
      if (!e.features?.length) return;
      const id = e.features[0].properties?.id;
      const cohort = dataRef.current.cohorts.find((c) => c.id === id);
      if (cohort) callbacksRef.current.onCohortClick?.(cohort);
    });

    // Warehouse click — look up full entity from dataRef
    map.on('click', 'warehouse-circles', (e) => {
      if (!e.features?.length) return;
      const id = e.features[0].properties?.id;
      const warehouse = dataRef.current.warehouses.find((w) => w.id === id);
      if (warehouse) callbacksRef.current.onWarehouseClick?.(warehouse);
    });

    // Hover cursors
    const hoverLayers = [
      'fields-fill', 'fields-center', 'tree-points', 'tree-clusters',
      'farmers-fill', 'cohorts-fill', 'warehouse-circles',
    ];
    for (const layerId of hoverLayers) {
      map.on('mouseenter', layerId, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = '';
      });
    }
  }, []);

  // ── Navigate on explicit request ────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !navigateRequested) return;
    navigatingRef.current = true;
    safeMapOperation(() => {
      map.flyTo({
        center: viewport.center,
        zoom: viewport.zoom,
        bearing: viewport.bearing,
        pitch: viewport.pitch,
        duration: 500,
        essential: true,
      });
    }, 'navigate');
  }, [navigateRequested, viewport.center, viewport.zoom, viewport.bearing, viewport.pitch]);

  // ── Update basemap style ────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const targetStyle = BASEMAP_STYLES[basemap];
    if (appliedStyleRef.current === targetStyle) return;
    safeMapOperation(() => {
      map.setStyle(targetStyle);
      appliedStyleRef.current = targetStyle;
      // style.load handler will call restoreAllData
    }, 'basemapChange');
  }, [basemap]);

  // ── Update data ─────────────────────────────────────────────
  // Each effect ensures the source+layers exist before pushing data,
  // so data is always displayed regardless of whether `style.load`
  // (which calls `restoreAllData`) has already run.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    safeMapOperation(() => {
      addFieldLayers(map);
      updateFieldData(map, fields);
    }, 'updateFields');
  }, [fields, styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    safeMapOperation(() => {
      addTreeLayers(map);
      updateTreeData(map, trees);
    }, 'updateTrees');
  }, [trees, styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    safeMapOperation(() => {
      addFarmerLayers(map);
      updateFarmerData(map, farmers);
    }, 'updateFarmers');
  }, [farmers, styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    safeMapOperation(() => {
      addCohortLayers(map);
      updateCohortData(map, cohorts);
    }, 'updateCohorts');
  }, [cohorts, styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    safeMapOperation(() => {
      addWarehouseLayers(map);
      updateWarehouseData(map, warehouses);
    }, 'updateWarehouses');
  }, [warehouses, styleVersion]);

  // ── Update visibility ───────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const setLayerVisibility = (layerId: string, visible: boolean) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
      }
    };

    setLayerVisibility('fields-fill', visibility.fields);
    setLayerVisibility('fields-line', visibility.fields);
    setLayerVisibility('fields-label', visibility.fields);
    setLayerVisibility('fields-center', visibility.fields);
    setLayerVisibility('tree-clusters', visibility.trees);
    setLayerVisibility('tree-cluster-count', visibility.trees);
    setLayerVisibility('tree-points', visibility.trees);
    setLayerVisibility('farmers-fill', visibility.farmers);
    setLayerVisibility('farmers-line', visibility.farmers);
    setLayerVisibility('cohorts-fill', visibility.cohorts);
    setLayerVisibility('cohorts-line', visibility.cohorts);
    setLayerVisibility('cohorts-label', visibility.cohorts);
    setLayerVisibility('warehouse-circles', visibility.warehouses);
    setLayerVisibility('warehouse-labels', visibility.warehouses);
  }, [visibility, styleVersion]);

  // ── Admin boundaries ────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    setAdminBoundaryLevel(map, adminBoundaryLevel);
  }, [adminBoundaryLevel, styleVersion]);

  // ── Selection state ─────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    setFieldSelected(map, selectedFieldId ?? null);
  }, [selectedFieldId, styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    setTreeSelected(map, selectedTreeId ?? null);
  }, [selectedTreeId, styleVersion]);

  // ── 3D terrain mode ──────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    if (mode === 'terrain3d') {
      map.easeTo({ pitch: 60, bearing: -17.6, duration: 500 });
      try {
        if (!map.getSource('mapbox-dem')) {
          map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          });
        }
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
        map.setFog({
          range: [0.5, 10],
          color: 'rgba(200, 210, 220, 0.4)',
          'horizon-blend': 0.1,
        });
      } catch {
        console.warn('[MapCanvas] Terrain not available with current token');
      }
    } else {
      map.easeTo({ pitch: 0, bearing: 0, duration: 500 });
      try {
        map.setTerrain(null);
        map.setFog(null);
      } catch { /* ignore */ }
    }
  }, [mode]);

  // ── Drawing mode ────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (drawingMode) {
      map.getCanvas().style.cursor = 'crosshair';
      ensureDrawLayers(map);
      updateDrawData(map, draftBoundary);

      // Click handler for drawing — GeoCoord is [lng, lat]
      const handleClick = (e: mapboxgl.MapMouseEvent) => {
        if (!drawRef.current.drawingMode) return;
        const { lat, lng } = e.lngLat;
        const current = [...drawRef.current.draftBoundary, [lng, lat] as GeoCoord];
        drawRef.current.onDraftBoundaryChange?.(current);
        updateDrawData(map, current);
      };

      // Right-click to undo last point
      const handleContextMenu = (e: mapboxgl.MapMouseEvent) => {
        if (!drawRef.current.drawingMode) return;
        e.preventDefault();
        const current = drawRef.current.draftBoundary.slice(0, -1);
        drawRef.current.onDraftBoundaryChange?.(current);
        updateDrawData(map, current);
      };

      map.on('click', handleClick);
      map.on('contextmenu', handleContextMenu);

      return () => {
        map.off('click', handleClick);
        map.off('contextmenu', handleContextMenu);
        map.getCanvas().style.cursor = '';
      };
    } else {
      removeDrawLayers(map);
      map.getCanvas().style.cursor = '';
    }
  }, [drawingMode, draftBoundary]);

  // Update draw data when draftBoundary changes externally
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !drawingMode || !map.isStyleLoaded()) return;
    ensureDrawLayers(map);
    updateDrawData(map, draftBoundary);
  }, [draftBoundary, drawingMode]);

  // ── Render ──────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className={`w-full h-full ${className}`}
      style={{ minHeight: '100%' }}
    />
  );
};

export default MapCanvas;
